<template>
  <div class="an">
    <div class="an-header">
      <h2 class="an-title">Website Analytics</h2>
      <div class="an-range">
        <button
          v-for="opt in ranges"
          :key="opt.days"
          class="an-range-btn"
          :class="{ active: days === opt.days }"
          @click="setRange(opt.days)"
        >{{ opt.label }}</button>
      </div>
    </div>

    <div v-if="loading" class="an-loading">Loading analytics…</div>

    <template v-else>
      <!-- Headline numbers -->
      <div class="an-cards">
        <div class="an-card">
          <div class="an-card-label">Total Page Views</div>
          <div class="an-card-value">{{ stats.total_visits.toLocaleString() }}</div>
        </div>
        <div class="an-card">
          <div class="an-card-label">Unique Visitors</div>
          <div class="an-card-value">{{ stats.unique_visitors.toLocaleString() }}</div>
        </div>
        <div class="an-card">
          <div class="an-card-label">Top Country</div>
          <div class="an-card-value">{{ topCountry }}</div>
        </div>
        <div class="an-card">
          <div class="an-card-label">Top Source</div>
          <div class="an-card-value">{{ topSource }}</div>
        </div>
      </div>

      <div v-if="stats.total_visits === 0" class="an-empty">
        No visits recorded yet for this period. Once the site is live and people
        browse it, data will appear here automatically.
      </div>

      <template v-else>
        <!-- Daily trend -->
        <div class="an-block">
          <h3 class="an-block-title">Visits per day</h3>
          <div class="an-chart">
            <div v-for="(d, i) in stats.daily" :key="i" class="an-bar-wrap" :title="`${d.day}: ${d.views}`">
              <div class="an-bar" :style="{ height: barHeight(d.views) }"></div>
              <span class="an-bar-day">{{ shortDay(d.day) }}</span>
            </div>
          </div>
        </div>

        <div class="an-grid">
          <!-- Countries -->
          <div class="an-block">
            <h3 class="an-block-title">Top countries</h3>
            <div v-for="row in stats.by_country" :key="row.country" class="an-row">
              <span class="an-row-label">{{ flag(row.country) }} {{ row.country }}</span>
              <div class="an-row-bar"><div :style="{ width: pct(row.views, stats.by_country) }"></div></div>
              <span class="an-row-val">{{ row.views }}</span>
            </div>
          </div>

          <!-- Sources -->
          <div class="an-block">
            <h3 class="an-block-title">Where visitors come from</h3>
            <div v-for="row in stats.by_source" :key="row.source" class="an-row">
              <span class="an-row-label">{{ row.source }}</span>
              <div class="an-row-bar"><div :style="{ width: pct(row.views, stats.by_source) }"></div></div>
              <span class="an-row-val">{{ row.views }}</span>
            </div>
          </div>

          <!-- Pages -->
          <div class="an-block">
            <h3 class="an-block-title">Most visited pages</h3>
            <div v-for="row in stats.top_pages" :key="row.path" class="an-row">
              <span class="an-row-label" :title="row.path">{{ row.path }}</span>
              <div class="an-row-bar"><div :style="{ width: pct(row.views, stats.top_pages) }"></div></div>
              <span class="an-row-val">{{ row.views }}</span>
            </div>
          </div>

          <!-- Devices -->
          <div class="an-block">
            <h3 class="an-block-title">Devices</h3>
            <div v-for="row in stats.by_device" :key="row.device" class="an-row">
              <span class="an-row-label an-cap">{{ row.device }}</span>
              <div class="an-row-bar"><div :style="{ width: pct(row.views, stats.by_device) }"></div></div>
              <span class="an-row-val">{{ row.views }}</span>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import api from "@/services/api";

interface Row { [k: string]: any; views: number }
interface Stats {
  total_visits: number;
  unique_visitors: number;
  by_country: Row[];
  by_source: Row[];
  top_pages: Row[];
  by_device: Row[];
  daily: Row[];
}

const emptyStats: Stats = {
  total_visits: 0, unique_visitors: 0,
  by_country: [], by_source: [], top_pages: [], by_device: [], daily: [],
};

export default defineComponent({
  name: "AnalyticsPanel",
  setup() {
    const stats = ref<Stats>({ ...emptyStats });
    const loading = ref(true);
    const days = ref(30);
    const ranges = [
      { days: 7, label: "7 days" },
      { days: 30, label: "30 days" },
      { days: 90, label: "90 days" },
    ];

    async function load() {
      loading.value = true;
      try {
        const { data } = await api.get(`/api/admin/analytics?days=${days.value}`);
        stats.value = { ...emptyStats, ...data };
      } catch {
        stats.value = { ...emptyStats };
      } finally {
        loading.value = false;
      }
    }

    function setRange(d: number) {
      days.value = d;
      load();
    }

    const topCountry = computed(() => stats.value.by_country[0]?.country ?? "—");
    const topSource = computed(() => stats.value.by_source[0]?.source ?? "—");

    const maxDaily = computed(() =>
      Math.max(1, ...stats.value.daily.map((d) => d.views))
    );
    function barHeight(v: number) {
      return `${Math.max(4, Math.round((v / maxDaily.value) * 100))}%`;
    }
    function pct(v: number, rows: Row[]) {
      const max = Math.max(1, ...rows.map((r) => r.views));
      return `${Math.max(3, Math.round((v / max) * 100))}%`;
    }
    function shortDay(day: string) {
      const d = new Date(day);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    }
    function flag(code: string) {
      if (!code || code.length !== 2) return "🌐";
      return code.toUpperCase().replace(/./g, (c) =>
        String.fromCodePoint(127397 + c.charCodeAt(0))
      );
    }

    onMounted(load);

    return {
      stats, loading, days, ranges, setRange,
      topCountry, topSource, barHeight, pct, shortDay, flag,
    };
  },
});
</script>

<style scoped>
.an { max-width: 1100px; }
.an-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; margin-bottom: 1.5rem; }
.an-title { color: #fff; font-weight: 800; font-size: 1.5rem; margin: 0; }
.an-range { display: flex; gap: 0.4rem; }
.an-range-btn { background: #201516; border: 1px solid #3a2526; color: #c0c0c0; font-size: 0.82rem; font-family: inherit; padding: 0.4rem 0.9rem; border-radius: 30px; cursor: pointer; transition: all 0.2s; }
.an-range-btn:hover { border-color: #c90e0e; color: #fff; }
.an-range-btn.active { background: #c90e0e; border-color: #c90e0e; color: #fff; }

.an-loading, .an-empty { color: #a0a0a0; background: #201516; border: 1px solid #3a2526; border-radius: 10px; padding: 2rem; text-align: center; }

.an-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
.an-card { background: #201516; border: 1px solid #3a2526; border-radius: 12px; padding: 1.25rem; }
.an-card-label { color: #888; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.5rem; }
.an-card-value { color: #fff; font-size: 1.6rem; font-weight: 800; }

.an-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 1rem; }
.an-block { background: #201516; border: 1px solid #3a2526; border-radius: 12px; padding: 1.25rem 1.5rem; margin-bottom: 1rem; }
.an-block-title { color: #fff; font-size: 0.95rem; font-weight: 700; margin: 0 0 1rem; }

.an-row { display: grid; grid-template-columns: 140px 1fr 44px; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; }
.an-row-label { color: #d0d0d0; font-size: 0.84rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.an-cap { text-transform: capitalize; }
.an-row-bar { background: #2b1e1f; border-radius: 6px; height: 10px; overflow: hidden; }
.an-row-bar > div { background: linear-gradient(90deg, #c90e0e, #ff5a5a); height: 100%; border-radius: 6px; }
.an-row-val { color: #a0a0a0; font-size: 0.82rem; text-align: right; }

.an-chart { display: flex; align-items: flex-end; gap: 3px; height: 140px; padding-top: 0.5rem; }
.an-bar-wrap { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; }
.an-bar { width: 100%; max-width: 24px; background: linear-gradient(180deg, #ff5a5a, #c90e0e); border-radius: 4px 4px 0 0; min-height: 4px; transition: height 0.3s; }
.an-bar-day { color: #777; font-size: 0.62rem; margin-top: 4px; white-space: nowrap; }
</style>
